import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';

interface Props {
  open: boolean;
  onClose: () => void;
  onSubmitted?: () => void;
}

const TAGS = ['NOTICE', 'EVENT', 'VOLUNTEER', 'CLUB', 'COURSE', 'LOST & FOUND'];
const MAX_BODY = 500;

export default function BulletinCompose({ open, onClose, onSubmitted }: Props) {
  const [submitterName, setSubmitterName] = useState('');
  const [tag, setTag] = useState(TAGS[0]);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [meta, setMeta] = useState('');
  const [link, setLink] = useState('');
  const [contact, setContact] = useState('');
  const [trap, setTrap] = useState('');
  const [status, setStatus] = useState<'idle' | 'sending' | 'done' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  useEffect(() => {
    if (open) {
      setStatus('idle');
      setFormError('');
    }
  }, [open]);

  if (!open) return null;

  const reset = () => {
    setSubmitterName('');
    setTag(TAGS[0]);
    setTitle('');
    setBody('');
    setMeta('');
    setLink('');
    setContact('');
    setTrap('');
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError('');

    // Honeypot — silently accept without writing.
    if (trap.trim()) {
      reset();
      setStatus('done');
      return;
    }

    if (!title.trim() || !body.trim()) {
      setStatus('error');
      setFormError('Please add a headline and a message for your notice.');
      return;
    }
    if (body.length > MAX_BODY) {
      setStatus('error');
      setFormError(`Your message is too long — keep it under ${MAX_BODY} characters.`);
      return;
    }

    setStatus('sending');
    try {
      const { data: top } = await supabase
        .from('gs_bulletins')
        .select('sort_order')
        .order('sort_order', { ascending: false })
        .limit(1);
      const nextOrder = ((top?.[0]?.sort_order as number) ?? 0) + 1;

      const { error } = await supabase.from('gs_bulletins').insert({
        tag,
        title: title.trim(),
        body: body.trim(),
        meta: meta.trim() || null,
        link: link.trim() || null,
        contact: contact.trim() || null,
        submitter_name: submitterName.trim() || null,
        sort_order: nextOrder,
        status: 'pending',
      });
      if (error) throw error;

      reset();
      setStatus('done');
      onSubmitted?.();
    } catch (err) {
      setStatus('error');
      setFormError(err instanceof Error ? err.message : 'Could not submit your notice. Please try again.');
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center p-0 sm:p-4" role="dialog" aria-modal="true" aria-label="Pin your own notice">
      <div className="absolute inset-0 bg-foreground-950/60 backdrop-blur-sm" onClick={onClose} />

      <div className="relative w-full sm:max-w-lg max-h-[92vh] overflow-y-auto bg-background-50 rounded-t-xl sm:rounded-lg border border-background-200">
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center text-foreground-500 hover:text-foreground-900 hover:bg-background-100 cursor-pointer transition-colors z-10"
        >
          <i className="ri-close-line text-xl"></i>
        </button>

        {status === 'done' ? (
          <div className="p-8 md:p-10 text-center">
            <span className="mx-auto w-14 h-14 rounded-full bg-primary-50 text-primary-600 flex items-center justify-center">
              <i className="ri-checkbox-circle-line text-3xl"></i>
            </span>
            <h3 className="mt-5 font-heading font-bold text-2xl text-foreground-950">Pinned to the queue</h3>
            <p className="mt-3 text-foreground-700 text-[15px] leading-relaxed">
              Thanks for sharing. A moderator reviews every notice before it goes up, so yours will appear on the board once it&apos;s approved.
            </p>
            <button
              type="button"
              onClick={onClose}
              className="mt-6 inline-flex items-center justify-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 font-label font-bold uppercase tracking-wider text-sm px-6 py-3 rounded-md whitespace-nowrap cursor-pointer transition-colors"
            >
              Back to the board
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 md:p-8">
            <span className="inline-flex items-center gap-2 bg-accent-100 text-accent-900 font-label font-semibold uppercase text-[11px] tracking-[0.16em] px-2.5 py-1 rounded-sm">
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-pushpin-2-line"></i></span>
              Pin a notice
            </span>
            <h3 className="mt-4 font-heading font-bold text-2xl md:text-3xl leading-tight text-foreground-950">Post to the Community Board</h3>
            <p className="mt-3 text-sm text-foreground-600 leading-relaxed">
              Share a local notice with the fairways. Add your link or contact info so neighbors can follow up, and we&apos;ll review it before it&apos;s published.
            </p>

            <div className="mt-6 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <label className="block">
                  <span className="font-label uppercase tracking-widest text-[11px] font-semibold text-foreground-600">Your name</span>
                  <input
                    value={submitterName}
                    onChange={(e) => setSubmitterName(e.target.value)}
                    placeholder="e.g. Dana R."
                    className="mt-1.5 w-full bg-background-50 border border-background-300 rounded-md px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-400"
                  />
                </label>
                <label className="block">
                  <span className="font-label uppercase tracking-widest text-[11px] font-semibold text-foreground-600">Notice type</span>
                  <select
                    value={tag}
                    onChange={(e) => setTag(e.target.value)}
                    className="mt-1.5 w-full bg-background-50 border border-background-300 rounded-md px-3.5 py-2.5 text-sm text-foreground-900 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-400 cursor-pointer"
                  >
                    {TAGS.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </label>
              </div>

              <label className="block">
                <span className="font-label uppercase tracking-widest text-[11px] font-semibold text-foreground-600">Headline</span>
                <input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  required
                  maxLength={120}
                  placeholder="e.g. Looking for a fourth for Saturday"
                  className="mt-1.5 w-full bg-background-50 border border-background-300 rounded-md px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-400"
                />
              </label>

              <label className="block">
                <span className="flex items-center justify-between font-label uppercase tracking-widest text-[11px] font-semibold text-foreground-600">
                  Message
                  <span className={body.length > MAX_BODY ? 'text-primary-700' : 'text-foreground-400'}>{body.length}/{MAX_BODY}</span>
                </span>
                <textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value.slice(0, MAX_BODY))}
                  required
                  maxLength={MAX_BODY}
                  rows={4}
                  placeholder="Tell neighbors what's happening, when and where."
                  className="mt-1.5 w-full bg-background-50 border border-background-300 rounded-md px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-400 resize-none"
                />
              </label>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <label className="block">
                  <span className="font-label uppercase tracking-widest text-[11px] font-semibold text-foreground-600">Location</span>
                  <input
                    value={meta}
                    onChange={(e) => setMeta(e.target.value)}
                    placeholder="e.g. Long Beach"
                    className="mt-1.5 w-full bg-background-50 border border-background-300 rounded-md px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-400"
                  />
                </label>
                <label className="block">
                  <span className="font-label uppercase tracking-widest text-[11px] font-semibold text-foreground-600">Link (optional)</span>
                  <input
                    value={link}
                    onChange={(e) => setLink(e.target.value)}
                    placeholder="https://..."
                    className="mt-1.5 w-full bg-background-50 border border-background-300 rounded-md px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-400"
                  />
                </label>
              </div>

              <label className="block">
                <span className="font-label uppercase tracking-widest text-[11px] font-semibold text-foreground-600">Contact info</span>
                <input
                  value={contact}
                  onChange={(e) => setContact(e.target.value)}
                  placeholder="Email, phone or website so neighbors can reach you"
                  className="mt-1.5 w-full bg-background-50 border border-background-300 rounded-md px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-400"
                />
              </label>
            </div>

            {/* Anti-spam honeypot */}
            <input
              type="text"
              name="website_alt"
              value={trap}
              onChange={(e) => setTrap(e.target.value)}
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
              className="field-trap"
            />

            {status === 'error' && formError && (
              <p className="mt-4 flex items-start gap-2 text-sm text-primary-700 bg-primary-50 border border-primary-200 rounded-md px-3.5 py-2.5">
                <span className="w-4 h-4 flex items-center justify-center shrink-0 mt-0.5"><i className="ri-error-warning-line"></i></span>
                {formError}
              </p>
            )}

            <div className="mt-6 flex flex-col sm:flex-row items-center gap-3">
              <button
                type="submit"
                disabled={status === 'sending'}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-primary-500 hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed text-background-50 font-label font-bold uppercase tracking-wider text-sm px-6 py-3 rounded-md whitespace-nowrap cursor-pointer transition-colors"
              >
                {status === 'sending' ? (
                  <>
                    <span className="w-4 h-4 flex items-center justify-center animate-spin"><i className="ri-loader-4-line"></i></span>
                    Sending
                  </>
                ) : (
                  <>
                    <span className="w-4 h-4 flex items-center justify-center"><i className="ri-pushpin-2-line"></i></span>
                    Submit for review
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="w-full sm:w-auto inline-flex items-center justify-center font-label font-semibold uppercase tracking-wider text-sm px-6 py-3 rounded-md whitespace-nowrap cursor-pointer text-foreground-700 hover:text-foreground-950 border border-background-300 hover:border-background-400 transition-colors"
              >
                Cancel
              </button>
            </div>
            <p className="mt-3 font-label uppercase tracking-widest text-[10px] text-foreground-500">Notices are reviewed before they are published.</p>
          </form>
        )}
      </div>
    </div>
  );
}